import { FileSource, UploadedFile, UploadedFiles } from "@/domain/file";
import { PaymentProvider } from "@/domain/repayments";
import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { BehaviorSubject } from "rxjs";
import { APP_CONFIG } from "../config/environment";
import { AuthService } from "./auth.service";
import { RepaymentsService } from "./repayments.service";

interface RequestOptions {
  signal?: AbortSignal;
  onProgress?: (completed: number, total: number) => void;
  /** Bypass the short-lived file cache when durable status determines UI state. */
  refresh?: boolean;
}

interface RequestContext {
  controller: AbortController;
  signal: AbortSignal;
}

interface PresignedUploadResponse {
  uploadUrl: string;
  key: string;
  expiresInSeconds: number;
  requiredHeaders: Record<string, string>;
}

const MAX_UPLOAD_SIZE_BYTES = 50 * 1024 * 1024;
const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".xls": "application/vnd.ms-excel",
  ".xlsb": "application/vnd.ms-excel.sheet.binary.macroEnabled.12",
  ".csv": "text/csv",
  ".txt": "text/plain",
};

@Injectable({
  providedIn: "root",
})
export class UploadedFileService implements OnDestroy {
  files: UploadedFiles = [];
  loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public readonly files$ = new BehaviorSubject<UploadedFiles>(this.files);
  private readonly requestControllers = new Map<string, AbortController>();
  private readonly fileCache = new Map<
    string,
    { file: UploadedFile; expires: number }
  >();
  private readonly cacheTTL = 5 * 60 * 1000; // 5 minutes
  private readonly postgrestBaseUrl = APP_CONFIG.postgrestBaseUrl;
  private readonly lambdaAuthUrl = APP_CONFIG.lambdaAuthUrl;
  private readonly swaggerPostgrestJwtLambdaName =
    APP_CONFIG.swaggerPostgrestJwtLambdaName;
  private readonly uploadToS3LambdaName = APP_CONFIG.uploadToS3LambdaName;
  private readonly generateDownloadUrlLambdaName =
    APP_CONFIG.generateDownloadUrlLambdaName;

  constructor(
    private http: HttpClient,
    private msalService: MsalService,
    private readonly authService: AuthService,
    private readonly repaymentsService: RepaymentsService,
  ) {}

  private startRequest(key: string): AbortController {
    const existing = this.requestControllers.get(key);
    if (existing) {
      existing.abort();
    }

    const controller = new AbortController();
    this.requestControllers.set(key, controller);

    return controller;
  }

  private clearRequest(key: string, controller: AbortController) {
    const active = this.requestControllers.get(key);
    if (active === controller) {
      this.requestControllers.delete(key);
    }
  }

  public cancelAllRequests(): void {
    const keys = Array.from(this.requestControllers.keys());
    keys.forEach((key) => {
      const controller = this.requestControllers.get(key);
      if (controller) {
        try {
          controller.abort();
        } catch {
          // ignore abort errors
        }
        this.requestControllers.delete(key);
      }
    });
  }

  private isAbortError(error: unknown): boolean {
    return (
      !!error &&
      typeof error === "object" &&
      "name" in error &&
      (error as { name?: string }).name === "AbortError"
    );
  }

  private createRequestContext(
    key: string,
    externalSignal?: AbortSignal,
  ): RequestContext {
    const controller = this.startRequest(key);

    if (externalSignal) {
      if (externalSignal.aborted) {
        controller.abort(externalSignal.reason);
      } else {
        const abort = () => controller.abort(externalSignal.reason);
        externalSignal.addEventListener("abort", abort, { once: true });
        controller.signal.addEventListener(
          "abort",
          () => externalSignal.removeEventListener("abort", abort),
          { once: true },
        );
      }
    }

    return { controller, signal: controller.signal };
  }

  ngOnDestroy(): void {
    this.requestControllers.forEach((controller) => controller.abort());
    this.requestControllers.clear();
  }

  public async getFile(
    fileID: string,
    options?: RequestOptions,
  ): Promise<UploadedFile | null> {
    if (options?.refresh) {
      await this.loadFileByID(fileID, options);
      return (
        this.getCachedFile(fileID) ||
        this.files.find((file) => file.id === fileID) ||
        null
      );
    }

    let file = this.getCachedFile(fileID);

    if (!file) {
      file = this.files.find((file) => file.id === fileID) || null;
      if (file) {
        this.memoizeFile(file);
      }
    }

    if (!file) {
      await this.loadFileByID(fileID, options);
      file =
        this.getCachedFile(fileID) ||
        this.files?.find((f) => f.id === fileID) ||
        null;
    }

    return file || null;
  }

  public loadAllFiles(options?: RequestOptions): Promise<void> {
    const { controller, signal } = this.createRequestContext(
      "loadAllFiles",
      options?.signal,
    );

    return this.authService
      .getPostgrestJwtToken(signal)
      .then((token) =>
        fetch(`${this.postgrestBaseUrl}/uploaded_file_with_repayment_count`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        }),
      )
      .then((postgrestResponse) => {
        return postgrestResponse.json();
      })
      .then((files) => {
        this.updateUploadedFileCache(files);
      })
      .catch((error) => {
        // Callers must be able to distinguish a completed empty response from
        // a request that navigation superseded. Treating an abort as success
        // lets the Uploaded Files view render its empty state from a cache
        // that has never been loaded.
        throw error;
      })
      .finally(() => {
        this.clearRequest("loadAllFiles", controller);
      });
  }

  public async loadFileByID(
    fileID: string,
    options?: RequestOptions,
  ): Promise<any> {
    const { controller, signal } = this.createRequestContext(
      "loadFileByID",
      options?.signal,
    );

    try {
      const postgrestResponse = await fetch(
        `${this.postgrestBaseUrl}/uploaded_file_with_repayment_count?id=eq.${fileID}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${await this.authService.getPostgrestJwtToken(
              signal,
            )}`,
          },
          signal,
        },
      );

      const newFile: UploadedFile = (await postgrestResponse.json())?.[0];

      const oldFile: UploadedFile = this.files.find((f) => f.id === fileID);

      if (!oldFile) {
        this.updateUploadedFileCache([newFile, ...this.files]);
      } else {
        Object.keys(oldFile).forEach((key) => {
          oldFile[key] = newFile[key];
        });
        this.emitFilesUpdate();
      }

      if (newFile) {
        this.memoizeFile(newFile);
      }
    } catch (error) {
      if (!this.isAbortError(error)) {
        throw error;
      }
    } finally {
      this.clearRequest("loadFileByID", controller);
    }
  }

  async uploadFileViaLambda(
    file: File,
    options?: RequestOptions,
  ): Promise<string> {
    const { controller, signal } = this.createRequestContext(
      "uploadFileViaLambda",
      options?.signal,
    );

    try {
      const contentType = this.getUploadContentType(file);
      this.validateUploadFile(file, contentType);

      const res = await fetch(this.lambdaAuthUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${await this.authService.getAzureAccessToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          lambdaName: this.uploadToS3LambdaName,
          payload: {
            requestUploadUrl: true,
            fileName: file.name,
            contentType,
            contentLength: file.size,
          },
        }),
        signal,
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Upload failed: ${res.status} - ${errorText}`);
      }

      const result = this.parseLambdaResponse(
        await res.json(),
      ) as Partial<PresignedUploadResponse>;
      if (
        !result.uploadUrl ||
        !result.key ||
        !result.requiredHeaders ||
        typeof result.requiredHeaders !== "object"
      ) {
        throw new Error("Upload service returned an invalid presigned URL");
      }

      const uploadResponse = await fetch(result.uploadUrl, {
        method: "PUT",
        headers: result.requiredHeaders,
        body: file,
        signal,
      });
      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed: ${uploadResponse.status}`);
      }

      // This key is durable. Presigned URLs must never be persisted because
      // they expire after five minutes.
      return result.key;
    } finally {
      this.clearRequest("uploadFileViaLambda", controller);
    }
  }

  async getPostgrestJwtToken(options?: RequestOptions): Promise<string> {
    const { controller, signal } = this.createRequestContext(
      "getPostgrestJwtToken",
      options?.signal,
    );

    try {
      const res = await fetch(this.lambdaAuthUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await this.authService.getAzureAccessToken()}`,
        },
        body: JSON.stringify({
          lambdaName: this.swaggerPostgrestJwtLambdaName,
          payload: {},
        }),
        signal,
      });

      if (!res.ok) {
        throw new Error(`Auth gateway failed: ${await res.text()}`);
      }

      const jwtToken = await res.json();

      return jwtToken;
    } finally {
      this.clearRequest("getPostgrestJwtToken", controller);
    }
  }

  async addFile(
    file: File,
    selectedCountry: string | { code?: string } | null | undefined,
    provider: PaymentProvider,
    options?: RequestOptions,
  ): Promise<string> {
    const { controller, signal } = this.createRequestContext(
      "addFile",
      options?.signal,
    );

    const created_by = this.authService.getUser();
    const download_url = await this.uploadFileViaLambda(file, { signal });
    const file_hash = await this.generateFileHash(file);
    const country = this.normalizeCountryCode(selectedCountry);

    const newFile: UploadedFile = {
      name: file.name,
      date: new Date().toISOString(),
      source: this.mapPaymentProviderToFileSource(provider),
      created_by,
      status: "UNSAVED",
      download_url: download_url,
      file_hash,
      country,
    };

    this.updateUploadedFileCache([newFile, ...this.files]);

    try {
      const postgrestResponse = await fetch(
        `${this.postgrestBaseUrl}/uploaded_files`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${await this.authService.getPostgrestJwtToken(
              signal,
            )}`,
            "Content-Type": "application/json",
            Prefer: "return=representation",
          },
          body: JSON.stringify(newFile),
          signal,
        },
      );

      const fileID = await postgrestResponse?.json();
      const createdID = fileID?.[0]?.id;
      if (createdID) {
        newFile.id = createdID;
        this.memoizeFile(newFile);
        this.emitFilesUpdate();
      }

      return createdID;
    } catch (error) {
      if (this.isAbortError(error)) {
        throw error;
      }
      console.error("Error adding file:", error);
      throw error;
    } finally {
      this.clearRequest("addFile", controller);
    }
  }

  async doesFileExist(file: File, options?: RequestOptions): Promise<string> {
    const { controller, signal } = this.createRequestContext(
      "doesFileExist",
      options?.signal,
    );

    const file_hash = await this.generateFileHash(file);
    try {
      const postgrestResponse = await fetch(
        // A completed file deletion retains its audit row with status DELETED.
        // It must not block a future upload of the same file contents.
        `${this.postgrestBaseUrl}/uploaded_files?file_hash=eq.${encodeURIComponent(file_hash)}&status=neq.DELETED`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${await this.authService.getPostgrestJwtToken(
              signal,
            )}`,
            "Content-Type": "application/json",
          },
          signal,
        },
      );
      const files = await postgrestResponse?.json();
      if (files && files.length > 0) {
        return files[0].id;
      } else {
        return null;
      }
    } catch (error) {
      if (this.isAbortError(error)) {
        throw error;
      }
      console.error("Error checking file existence:", error);
      throw error;
    } finally {
      this.clearRequest("doesFileExist", controller);
    }
  }

  async updateFile(file: UploadedFile, options?: RequestOptions) {
    const { controller, signal } = this.createRequestContext(
      "updateFile",
      options?.signal,
    );

    // Do not mutate the object used by the table. Mutating it caused the record
    // count to disappear until the next full page refresh.
    const payload = { ...file };
    delete payload.repayment_count;

    try {
      const response = await fetch(`${this.postgrestBaseUrl}/uploaded_files?id=eq.${file.id}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${await this.authService.getPostgrestJwtToken(
            signal,
          )}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        signal,
      });
      if (!response.ok) {
        throw new Error(
          `Failed to update uploaded file: ${response.status} ${response.statusText}`,
        );
      }
      this.updateUploadedFileCache([file]);
    } catch (error) {
      if (this.isAbortError(error)) {
        throw error;
      }
      console.error("Error updating file:", error);
      throw error;
    } finally {
      this.clearRequest("updateFile", controller);
    }
  }

  async deleteFiles(
    ids: Array<string>,
    options?: RequestOptions,
  ): Promise<void> {
    const { controller, signal } = this.createRequestContext(
      "deleteFiles",
      options?.signal,
    );

    try {
      // Remove repayments first. These calls soft-delete matching rows in both
      // tables, so a deleted file cannot leave visible staging repayments behind.
      await this.repaymentsService.deleteRepaymentsByFileID(ids, {
        signal,
        onProgress: options?.onProgress,
      });

      const response = await fetch(
        `${this.postgrestBaseUrl}/uploaded_files?id=in.(${ids.join(",")})`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${await this.authService.getPostgrestJwtToken(
              signal,
            )}`,
            "Content-Type": "application/json",
          },
          signal,
        },
      );
      if (!response.ok) {
        throw new Error(
          `Failed to delete uploaded file: ${response.status} ${response.statusText}`,
        );
      }
      this.removeFilesFromCache(ids);
      options?.onProgress?.(1, 1);
    } catch (error) {
      if (this.isAbortError(error)) {
        throw error;
      }
      console.error("Error deleting file:", error);
      throw error;
    } finally {
      this.clearRequest("deleteFiles", controller);
    }
  }

  public async generateFileHash(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    return hashHex;
  }

  public async getSignedDownloadURL(
    file: string,
    options?: RequestOptions,
    fileId?: string,
  ): Promise<string> {
    const { controller, signal } = this.createRequestContext(
      "getSignedDownloadURL",
      options?.signal,
    );

    try {
      const fileReference = await this.resolveDownloadReference(
        file,
        fileId,
        signal,
      );
      const res = await fetch(this.lambdaAuthUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await this.authService.getAzureAccessToken()}`,
        },
        body: JSON.stringify({
          lambdaName: this.generateDownloadUrlLambdaName,
          payload: {
            ...this.getDownloadObjectLocation(fileReference),
          },
        }),
        signal,
      });

      if (!res.ok) {
        throw new Error(`Auth gateway failed: ${await res.text()}`);
      }

      const result = this.parseLambdaResponse(await res.json()) as {
        downloadUrl?: string;
      };
      if (!result?.downloadUrl) {
        throw new Error("Download service returned no URL");
      }
      return result.downloadUrl;
    } finally {
      this.clearRequest("getSignedDownloadURL", controller);
    }
  }

  private getUploadContentType(file: File): string {
    const extension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
    return CONTENT_TYPE_BY_EXTENSION[extension] || file.type;
  }

  private validateUploadFile(file: File, contentType: string): void {
    const extension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
    if (!CONTENT_TYPE_BY_EXTENSION[extension] || !contentType) {
      throw new Error(
        "Only .xlsx, .xls, .xlsb, .csv, and .txt files can be uploaded",
      );
    }
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      throw new Error("Files must be 50 MiB or smaller");
    }
  }

  private getDownloadObjectLocation(fileReference: string): {
    bucket?: string;
    key: string;
  } {
    if (fileReference.startsWith("uploads/")) {
      return { key: fileReference };
    }

    if (fileReference.startsWith("s3://")) {
      try {
        const location = new URL(fileReference);
        const objectKey = location.pathname.replace(/^\/+/, "");
        if (objectKey) {
          return { bucket: location.hostname, key: decodeURIComponent(objectKey) };
        }
      } catch {
        // Fall back to the legacy filename format below.
      }
    }

    if (fileReference.startsWith("http")) {
      try {
        const objectKey = new URL(fileReference).pathname.replace(/^\/+/, "");
        if (objectKey) return { key: decodeURIComponent(objectKey) };
      } catch {
        // Fall back to the legacy filename format below.
      }
    }

    return { key: `uploads/${fileReference}` };
  }

  /**
   * Older Uploaded Files view definitions did not expose download_url. Fetch
   * the authoritative value only when that omission would otherwise make us
   * fall back to a filename and lose an external S3 prefix.
   */
  private async resolveDownloadReference(
    fileReference: string,
    fileId: string | undefined,
    signal: AbortSignal,
  ): Promise<string> {
    if (!fileId || fileReference.startsWith("s3://")) return fileReference;

    const response = await fetch(
      `${this.postgrestBaseUrl}/uploaded_files?id=eq.${encodeURIComponent(fileId)}&select=download_url`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${await this.authService.getPostgrestJwtToken(signal)}`,
        },
        signal,
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to resolve download location: ${response.status}`);
    }
    const [stored] = (await response.json()) as Array<{ download_url?: string }>;
    return stored?.download_url || fileReference;
  }

  private parseLambdaResponse(response: unknown): unknown {
    if (typeof response !== "string") {
      return response;
    }

    try {
      return JSON.parse(response);
    } catch {
      throw new Error("Upload service returned invalid JSON");
    }
  }

  public updateUploadedFileCache(newlyLoadedFiles: UploadedFiles) {
    const deletedIds = new Set(
      newlyLoadedFiles
        .filter((file) => file.status?.toUpperCase() === "DELETED")
        .map((file) => file.id)
        .filter((id): id is string => !!id),
    );
    if (deletedIds.size) {
      this.files = this.files.filter((file) => !deletedIds.has(file.id || ""));
      deletedIds.forEach((id) => this.fileCache.delete(id));
    }

    const visibleFiles = newlyLoadedFiles.filter(
      (file) => file.status?.toUpperCase() !== "DELETED",
    );
    if (this.files.length === 0) {
      this.files.push(...visibleFiles);
      visibleFiles.forEach((file) => this.memoizeFile(file));
      this.emitFilesUpdate();
      return;
    }

    // Loop through newly loaded data
    visibleFiles.forEach((file) => {
      // Find existing record for each of the loaded files
      const staleFile = this.files.find((r) => r.id === file.id);

      if (staleFile) {
        // Update only the fields that changed
        Object.keys(file).forEach((key) => {
          staleFile[key] = file[key];
        });
      } else {
        // Add new repayment to the existing array (in-place)
        this.files.push(file);
      }

      if (file) {
        this.memoizeFile(file);
      }
    });
    this.emitFilesUpdate();
  }

  public setFileRepaymentCount(fileID: string, repaymentCount: number): void {
    const file = this.files.find((candidate) => candidate.id === fileID);
    if (!file) {
      return;
    }
    file.repayment_count = repaymentCount;
    this.memoizeFile(file);
    this.emitFilesUpdate();
  }

  private emitFilesUpdate() {
    this.files$.next(this.files);
  }

  private normalizeCountryCode(
    selectedCountry: string | { code?: string } | null | undefined,
  ): string {
    if (typeof selectedCountry === "string") {
      return selectedCountry;
    }
    if (
      selectedCountry &&
      typeof selectedCountry === "object" &&
      typeof selectedCountry.code === "string"
    ) {
      return selectedCountry.code;
    }
    return "";
  }

  private mapPaymentProviderToFileSource(
    provider: PaymentProvider,
  ): FileSource {
    switch (provider) {
      case PaymentProvider.APS:
        return FileSource.ApsCashplus;
      case PaymentProvider.CARDONE:
        return FileSource.Cardone;
      case PaymentProvider.FDMS:
        return FileSource.Fdms;
      case PaymentProvider.GOCARDLESS:
        return FileSource.Gocardless;
      case PaymentProvider.DOJO_DIRECT_SPLIT:
        return FileSource.DojoDirectSplit;
      case PaymentProvider.MODULR:
        return FileSource.Modulr;
      case PaymentProvider.CUSTOM:
        return FileSource.Custom;
      case PaymentProvider.CARDNET:
      default:
        return FileSource.Cardnet;
    }
  }

  private removeFilesFromCache(ids: Array<string>) {
    if (!ids?.length) {
      return;
    }
    for (let i = this.files.length - 1; i >= 0; i -= 1) {
      if (ids.includes(this.files[i]?.id)) {
        this.files.splice(i, 1);
      }
    }
    ids.forEach((id) => this.fileCache.delete(id));
    this.emitFilesUpdate();
  }

  private memoizeFile(file: UploadedFile | null | undefined) {
    if (!file?.id) {
      return;
    }
    this.fileCache.set(file.id, {
      file,
      expires: Date.now() + this.cacheTTL,
    });
  }

  private getCachedFile(fileID: string): UploadedFile | null {
    const cached = this.fileCache.get(fileID);
    if (!cached) {
      return null;
    }

    if (cached.expires < Date.now()) {
      this.fileCache.delete(fileID);
      return null;
    }

    return cached.file;
  }
}
